import { ApiClient } from "@gladis/api-client";

import { readAccessToken } from "./auth-context.js";

export const CSRF_COOKIE_NAME = "__gladis_csrf";

/**
 * Reads the CSRF cookie.
 *
 * Script-readable on purpose — that is precisely how double-submit works. The browser sends the
 * cookie automatically, but only same-origin JavaScript can READ it and echo it back in the header.
 * A cross-site attacker can cause the cookie to be sent and still cannot produce the header.
 */
function readCsrfToken(): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Set by the app shell so the client can hand a renewed token back into React state, and route to
 * sign-in when the session is unrecoverable. Assigned rather than injected because the client is
 * created once at module scope while these depend on React context.
 */
export const authBridge: {
  setAccessToken: (token: string | undefined) => void;
  onUnauthenticated: () => void;
} = {
  setAccessToken: () => {},
  onUnauthenticated: () => {},
};

/**
 * Renews the access token from the refresh cookie.
 *
 * THE ONE IMPLEMENTATION. Both callers use it — the client's 401 retry and the cold-start
 * restore in main.tsx — because they had drifted: the cold-start copy omitted the CSRF header, so
 * it 403'd the moment a session cookie existed, which is to say on every reload after signing in.
 * Two copies of a request whose headers matter is one copy too many.
 *
 * Returns undefined rather than throwing on failure: the caller retries at most once and then
 * stops. Retrying on a revoked refresh token is the infinite loop that presents as a hung app.
 */
export async function restoreSession(): Promise<string | undefined> {
  const csrf = readCsrfToken();

  const response = await fetch("/api/auth/refresh", {
    method: "POST",
    credentials: "same-origin",
    headers: csrf ? { "x-gladis-csrf": csrf } : {},
  }).catch(() => null);

  if (!response?.ok) return undefined;

  const session = (await response.json()) as { accessToken: string };
  authBridge.setAccessToken(session.accessToken);
  return session.accessToken;
}

export const apiClient = new ApiClient({
  getAccessToken: readAccessToken,
  refreshAccessToken: restoreSession,
  getCsrfToken: readCsrfToken,
  onUnauthenticated: () => authBridge.onUnauthenticated(),
});
